<template>
  <Popup
    v-model:is-visible="isVisible"
    class="traffic-block-popup"
    :title="popupTitle"
    :width="500"
    @close="close()"
  >
    <template v-slot:body>
      <div
        v-loading="isDataLoading"
        class="t:space-y-16"
      >
        <div class="t:space-y-12">
          <CardTitle
            :title="$t('affiliates.trafficBlock.conditionsTitle')"
          >
            <UiRadioGroup
              v-model="form.conditionsMode"
              :options="[
                {
                  label: $t('affiliates.trafficBlock.conditions.allTraffic'),
                  value: 'all',
                },
                {
                  label: $t('affiliates.trafficBlock.conditions.onlySelected'),
                  value: 'selected',
                }
              ]"
            />
          </CardTitle>
          <div class="t:space-y-8">
            <UiCheckbox
              v-model="form.offer.enabled"
              :label="$t('affiliates.trafficBlock.conditions.offer')"
              :disabled="form.conditionsMode === 'all'"
            />
            <UiSelectElement
              v-model="form.offer.values"
              :placeholder="$t('affiliates.trafficBlock.conditions.offerPlaceholder')"
              :list="offerOptions"
              size="big"
              option-name-param="full_name"
              option-label-param="full_name"
              search-param="full_name"
              filterable
              multiple
              clearable
              collapse-tag-tooltip
              :max-collapse-tags="1"
              :error="errors.filter_selected?.offers_selected || errors.filter_selected?.offers_all"
              :disabled="form.conditionsMode === 'all' || !form.offer.enabled"
            />
          </div>
          <div class="t:space-y-8">
            <UiCheckbox
              v-model="form.trafficSource.enabled"
              :label="$t('affiliates.trafficBlock.conditions.trafficSource')"
              :disabled="form.conditionsMode === 'all'"
            />
            <UiSelectElement
              v-model="form.trafficSource.values"
              :placeholder="$t('affiliates.trafficBlock.conditions.trafficSourcePlaceholder')"
              :list="trafficSourceOptions"
              size="big"
              search-param="name"
              filterable
              multiple
              clearable
              collapse-tag-tooltip
              :max-collapse-tags="1"
              :error="errors.filter_selected?.traffic_sources_selected || errors.filter_selected?.traffic_sources_all"
              :disabled="form.conditionsMode === 'all' || !form.trafficSource.enabled"
            />
          </div>
          <div class="t:space-y-8">
            <UiCheckbox
              v-model="form.campaigns.enabled"
              :label="$t('affiliates.trafficBlock.conditions.mediaBuyingCampaign')"
              :disabled="form.conditionsMode === 'all'"
            />
            <UiSelectElement
              v-model="form.campaigns.values"
              :placeholder="$t('affiliates.trafficBlock.conditions.mediaBuyingCampaignPlaceholder')"
              :list="campaignOptions"
              size="big"
              search-param="name"
              option-name-param="label"
              option-label-param="label"
              filterable
              multiple
              clearable
              collapse-tag-tooltip
              :max-collapse-tags="1"
              :error="errors.filter_selected?.media_buying_campaigns_selected || errors.filter_selected?.media_buying_campaigns_all"
              :disabled="form.conditionsMode === 'all' || !form.campaigns.enabled"
            />
          </div>
        </div>
        <UiSeparator />
        <div class="t:space-y-12">
          <CardTitle
            :title="$t('affiliates.trafficBlock.durationTitle')"
          >
            <UiRadioGroup
              v-model="form.duration"
              :options="[
                {
                  label: $t('affiliates.trafficBlock.duration.permanent'),
                  value: 'permanent',
                },
                {
                  label: $t('affiliates.trafficBlock.duration.temporary'),
                  value: 'temporary',
                }
              ]"
            />
          </CardTitle>
          <UiDatePicker
            v-model="form.expiresAt"
            :placeholder="$t('affiliates.trafficBlock.duration.expiresAtPlaceholder')"
            :range="false"
            :multi-calendars="false"
            :disabled="form.duration === 'permanent'"
            external-format="dd MMM yyyy HH:mm"
            enable-time-picker
            teleport
            :auto-position="'top'"
            :min-date="new Date()"
            :error="errors.blocked_till"
          />
        </div>
        <UiTextarea
          v-model="form.reason"
          :label="$t('affiliates.trafficBlock.reason')"
          :placeholder="$t('affiliates.trafficBlock.reasonPlaceholder')"
          :maxlength="500"
          :rows="3"
        />
      </div>
    </template>
    <template v-slot:footer>
      <div class="t:flex t:items-center t:justify-between">
        <UiButton
          v-if="isEditMode"
          type="positive"
          size="big"
          icon="unlock"
          @click="openUnblockConfirm"
        >
          {{ $t('affiliates.trafficBlock.unblock.action') }}
        </UiButton>
        <div class="t:space-x-8 t:ml-auto">
          <UiButton
            type="negative"
            size="big"
            icon="times"
            @click="close()"
          >
            {{ $t('crm.buttons.cancel') }}
          </UiButton>
          <UiButton
            filled
            type="negative"
            size="big"
            :icon="isEditMode ? 'check' : 'arrow-down-up-lock'"
            :disabled="isSaveActionDisabled"
            @click="save"
          >
            {{ primaryActionLabel }}
          </UiButton>
        </div>
      </div>
    </template>
  </Popup>

  <UiConfirm
    ref="unblockConfirmRef"
    :width="487"
    :action="'unblockTraffic'"
    :action-name="$t('affiliates.trafficBlock.unblock.action')"
    action-fa-icon="unlock"
    :close-action-name="$t('crm.buttons.cancel')"
    :has-close-btn="true"
    @save="confirmUnblock"
  >
    <template v-slot:title>
      <div>{{ $t('affiliates.trafficBlock.unblock.title', { affiliateEmail }) }}</div>
    </template>
    <div class="body__msg t:flex t:items-center">
      <UiIcon
        :size="24"
        class="t:mr-12"
        name="unlock"
        color="var(--text-primary)"
      />
      <div v-html="$t('affiliates.trafficBlock.unblock.text', { affiliateEmail })" />
    </div>
  </UiConfirm>
</template>

<script lang="ts">
import { format } from 'date-fns';
import { defineComponent, ref } from 'vue';
import { useApi } from '@/api';
import { isErrorResponse } from '@/types/core';
import type { AxiosResponse } from 'axios';
import type {
  GetAffiliateOffersResponse,
  GetTrafficSourcesResponse,
  GetMediaBuyingCampaignsResponse,
  GetAffiliatesByIdTrafficBlockResponses,
  PutAffiliatesByIdTrafficBlockData,
} from '@/types/openapi-ts';

type FilterMode = PutAffiliatesByIdTrafficBlockData['body']['filter_mode'];
type TrafficBlockConfig = GetAffiliatesByIdTrafficBlockResponses[200]['payload'];
type OfferOption = Pick<GetAffiliateOffersResponse['payload'][number], 'id' | 'full_name'>;
type TrafficSourceOption = Pick<GetTrafficSourcesResponse['payload'][number], 'id' | 'name'>;
type CampaignOption = Pick<GetMediaBuyingCampaignsResponse['payload'][number], 'id' | 'name' | 'incrementing_id'>;

interface FilterSelected {
  offers_all?: boolean,
  offers_selected?: string[],
  traffic_sources_all?: boolean,
  traffic_sources_selected?: string[],
  media_buying_campaigns_all?: boolean,
  media_buying_campaigns_selected?: string[],
};

interface ErrorResponse {
  blocked_till?: string
  filter_mode?: string
  filter_selected?: FilterSelected
  is_permanent?: boolean
  reason?: string | null
};


export default defineComponent({
  name: 'TrafficBlockPopup',
  setup() {
    const api = useApi();
    const unblockConfirmRef = ref<{ open: () => void; close: () => void } | null>(null);

    return {
      api,
      unblockConfirmRef,
    };
  },
  props: {
    affiliateId: {
      type: String,
      required: true,
    },
    affiliateEmail: {
      type: String,
      default: '',
    },
    isBlocked: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['saved'],
  data() {
    return {
      trafficBlockConfig: null as TrafficBlockConfig | null,
      isDataLoading: false,
      isVisible: false,
      errors: {} as ErrorResponse,
      offerOptions: [] as Array<OfferOption | { id: 'all'; full_name: string }>,
      trafficSourceOptions: [] as Array<TrafficSourceOption | { id: 'all'; name: string }>,
      campaignOptions: [] as Array<CampaignOption | { id: 'all'; label: string }>,
      form: {
        conditionsMode: 'all' as FilterMode,
        offer: {
          enabled: false,
          values: [] as string[],
        },
        trafficSource: {
          enabled: false,
          values: [] as string[],
        },
        campaigns: {
          enabled: false,
          values: [] as string[],
        },
        duration: 'permanent' as 'permanent' | 'temporary',
        expiresAt: null as Date | null,
        reason: '' as string,
      },
    };
  },
  computed: {
    isEditMode(): boolean {
      return !!this.trafficBlockConfig?.id;
    },
    popupTitle(): string {
      return this.isEditMode
        ? this.$t('affiliates.trafficBlock.editTitle')
        : this.$t('affiliates.trafficBlock.title');
    },
    primaryActionLabel(): string {
      return this.isEditMode
        ? this.$t('affiliates.confirm.save')
        : this.$t('affiliates.trafficBlock.button.block');
    },
    isSaveActionDisabled(): boolean {
      if (this.form.conditionsMode !== 'selected') {
        return false;
      }

      return !this.form.offer.enabled
        && !this.form.trafficSource.enabled
        && !this.form.campaigns.enabled;
    },
  },
  watch: {
    isVisible: {
      immediate: true,
      async handler(val: boolean) {
        if (val) {
          this.resetForm();
          this.trafficBlockConfig = null;
          await this.initialLoad();
          this.applyInitialConfig();
        }
      },
    },
    'form.offer.values': {
      deep: true,
      handler(val: string[], oldVal: string[] = []) {
        if (!val?.includes('all')) return;
        if (!val?.some((item) => item !== 'all')) return;

        this.form.offer.values = (!oldVal?.includes('all')
          ? ['all']
          : val.filter((item) => item !== 'all')) as typeof this.form.offer.values;
      },
    },
    'form.trafficSource.values': {
      deep: true,
      handler(val: string[], oldVal: string[] = []) {
        if (!val?.includes('all')) return;
        if (!val?.some((item) => item !== 'all')) return;

        this.form.trafficSource.values = (!oldVal?.includes('all')
          ? ['all']
          : val.filter((item) => item !== 'all')) as typeof this.form.trafficSource.values;
      },
    },
    'form.campaigns.values': {
      deep: true,
      handler(val: string[], oldVal: string[] = []) {
        if (!val?.includes('all')) return;
        if (!val?.some((item) => item !== 'all')) return;

        this.form.campaigns.values = (!oldVal?.includes('all')
          ? ['all']
          : val.filter((item) => item !== 'all')) as typeof this.form.campaigns.values;
      },
    },
  },
  methods: {
    open() {
      this.isVisible = true;
    },
    resetForm() {
      this.errors = {};
      this.form.conditionsMode = 'all';
      this.form.offer.enabled = false;
      this.form.offer.values = [];
      this.form.trafficSource.enabled = false;
      this.form.trafficSource.values = [];
      this.form.campaigns.enabled = false;
      this.form.campaigns.values = [];
      this.form.duration = 'permanent';
      this.form.expiresAt = null;
      this.form.reason = '';
    },
    applyInitialConfig() {
      if (!this.trafficBlockConfig) return;

      const {
        filter_mode,
        filter_selected,
        is_permanent: isPermanent,
        blocked_till: blockedTill,
        reason,
      } = this.trafficBlockConfig;

      this.form = {
        conditionsMode: filter_mode,
        offer: {
          enabled: !!(filter_selected?.offers_selected?.length
            || filter_selected?.offers_all),
          values: filter_selected?.offers_all ? ['all'] : filter_selected?.offers_selected ?? [],
        },
        trafficSource: {
          enabled: !!(filter_selected?.traffic_sources_selected?.length
            || filter_selected?.traffic_sources_all),
          values: filter_selected?.traffic_sources_all ? ['all'] : filter_selected?.traffic_sources_selected ?? [],
        },
        campaigns: {
          enabled: !!(filter_selected?.media_buying_campaigns_selected?.length
            || filter_selected?.media_buying_campaigns_all),
          values: filter_selected?.media_buying_campaigns_all ? ['all'] : (filter_selected?.media_buying_campaigns_selected ?? []),
        },
        duration: isPermanent ? 'permanent' : 'temporary',
        expiresAt: blockedTill ? new Date(blockedTill) : null,
        reason: reason || '',
      };
    },
    async fetchTrafficBlockConfig() {
      if (!this.isBlocked) return;

      try {
        const res = await this.api.getAffiliateTrafficBlock(this.affiliateId);
        this.trafficBlockConfig = res?.data?.payload || null;
      } catch {
        this.trafficBlockConfig = null;
      }
    },
    async initialLoad() {
      this.isDataLoading = true;
      try {
        await Promise.all([
          this.fetchTrafficBlockConfig(),
          this.getAffiliateOffers(),
          this.getTrafficSources(),
          this.getMediaBuyingCampaigns(),
        ]);
      } finally {
        this.isDataLoading = false;
      }
    },
    getAffiliateOffers() {
      return this.api.getAffiliateOffers(this.affiliateId)
        .then((res: AxiosResponse<GetAffiliateOffersResponse>) => {
          this.offerOptions = [
            { id: 'all', full_name: this.$t('ui.filters.select.all') },
            ...res.data.payload,
          ];
        })
        .catch(() => {
          this.offerOptions = [{ id: 'all', full_name: this.$t('ui.filters.select.all') }];
        });
    },
    getTrafficSources() {
      return this.api.getTrafficSources({ affiliate_id: this.affiliateId })
        .then((res: AxiosResponse<GetTrafficSourcesResponse>) => {
          this.trafficSourceOptions = [
            { id: 'all', name: this.$t('ui.filters.select.all') },
            ...res.data.payload,
          ];
        })
        .catch(() => {
          this.trafficSourceOptions = [{ id: 'all', name: this.$t('ui.filters.select.all') }];
        });
    },
    getMediaBuyingCampaigns(search?: string) {
      return this.api.getMediaBuyingCampaigns({
        limit: 100,
        offset: 0,
        sort_column: 'created_at',
        sort_dir: 'desc',
        search: search || null,
        affiliate_id: this.affiliateId,
        root_campaign_id: 'null',
      })
        .then((res: AxiosResponse<GetMediaBuyingCampaignsResponse>) => {
          this.campaignOptions = [
            {
              id: 'all',
              label: this.$t('ui.filters.select.all'),
            },
            ...res.data.payload.map((campaign) => ({
              ...campaign,
              label: `(ID: ${campaign.incrementing_id}) ${campaign.name}`,
            })),
          ];
        })
        .catch(() => {
          this.campaignOptions = [{ id: 'all', label: this.$t('ui.filters.select.all') }];
        });
    },
    buildPayload(): PutAffiliatesByIdTrafficBlockData['body'] {
      const isPermanent = this.form.duration === 'permanent';
      const isSelectedMode = this.form.conditionsMode === 'selected';
      const blockedTill = !isPermanent && this.form.expiresAt
        ? format(this.form.expiresAt, 'yyyy-MM-dd HH:mm:ss')
        : null;

      return {
        filter_mode: this.form.conditionsMode,
        filter_selected: {
          ...(isSelectedMode && this.form.offer.enabled && {
            offers_all: this.form.offer.values?.[0] === 'all' ? true : null,
            offers_selected: this.form.offer.values?.[0] !== 'all' ? this.form.offer.values : null,
          }),
          ...(isSelectedMode && this.form.trafficSource.enabled && {
            traffic_sources_all: this.form.trafficSource.values?.[0] === 'all' ? true : null,
            traffic_sources_selected: this.form.trafficSource.values?.[0] !== 'all' ? this.form.trafficSource.values : null,
          }),
          ...(isSelectedMode && this.form.campaigns.enabled && {
            media_buying_campaigns_all: this.form.campaigns.values?.[0] === 'all' ? true : null,
            media_buying_campaigns_selected: this.form.campaigns.values?.[0] !== 'all' ? this.form.campaigns.values : null,
          }),
        },
        is_permanent: isPermanent,
        blocked_till: blockedTill,
        reason: this.form.reason ? this.form.reason.trim() : null,
      };
    },
    save() {
      const payload = this.buildPayload();

      this.isDataLoading = true;

      this.api.putAffiliateTrafficBlock(this.affiliateId, payload)
        .then(() => {
          this.$emit('saved');
          this.close();
        })
        .catch((error: unknown) => {
          if (isErrorResponse(error)) {
            this.errors = error.data.errors;
            return;
          }

          this.errors = {};
        })
        .finally(() => {
          this.isDataLoading = false;
        });
    },
    openUnblockConfirm() {
      this.unblockConfirmRef?.open();
    },
    confirmUnblock() {
      this.isDataLoading = true;

      this.api.deleteAffiliateTrafficBlock(this.affiliateId)
        .then(() => {
          this.$emit('saved');
          this.close();
        })
        .finally(() => {
          this.isDataLoading = false;
          this.unblockConfirmRef?.close();
        });
    },
    close() {
      this.isVisible = false;
    },
  },
});
</script>
